import { GoBackButton, PrivateLayout, Tag } from '@components';
import { GetServerSidePropsContext, NextLayoutComponentType } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';
import styles from '../styles/privacy.module.scss';

const PrivacyPage: NextLayoutComponentType = () => {
  const router = useRouter();
  const { t } = useTranslation('common');
  return (
    <div>
      <Head>
        <title>Privacy</title>
        <meta name="description" content="Privacy" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <div className={styles.container}>
        <div className={styles.goBackButton}>
          <GoBackButton onClick={() => router.back()} />
        </div>
        <Tag text={t('terms_and_conditions.tag')} mode="light" />
        <h1 className={styles.title}>{t('privacy_page.title')}</h1>

        <div className={styles.contentWrapper}>
          <h2 className={styles.subtitle}>{t('privacy_page.subtitle')}</h2>
          <div className={styles.address}>
            <h2>MANOR AG</h2>
            <h2>Rebgasse 34</h2>
            <h2>CH - 4058 Basel</h2>
          </div>
          <div className={styles.contact}>
            <h2>{t('contact')}</h2>
            <a
              href={`mailto:${process.env.NEXT_PUBLIC_SUPPORT_EMAIL}`}
              target="_blank"
              rel="noreferrer">
              {process.env.NEXT_PUBLIC_SUPPORT_EMAIL}
            </a>
          </div>

          <div className={styles.section}>
            <h3>{t('privacy_page.section_1.title')}</h3>
            <p>{t('privacy_page.section_1.paragraph_1')}</p>
            <p>{t('privacy_page.section_1.paragraph_2')}</p>
            <p>{t('privacy_page.section_1.paragraph_3')}</p>
            <p>{t('privacy_page.section_1.paragraph_4')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_2.title')}</h3>
            <p>{t('privacy_page.section_2.paragraph_1')}</p>
            <p>{t('privacy_page.section_2.paragraph_2')}</p>
            <p>{t('privacy_page.section_2.paragraph_3')}</p>
            <p>{t('privacy_page.section_2.paragraph_4')}</p>
            <p>{t('privacy_page.section_2.paragraph_5')}</p>
            <p>{t('privacy_page.section_2.paragraph_6')}</p>
            <p>{t('privacy_page.section_2.paragraph_7')}</p>
            <p>{t('privacy_page.section_2.paragraph_8')}</p>
            <p>{t('privacy_page.section_2.paragraph_9')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_3.title')}</h3>
            <p>{t('privacy_page.section_3.paragraph_1')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_4.title')}</h3>
            <p>{t('privacy_page.section_4.paragraph_1')}</p>
            <p>{t('privacy_page.section_4.paragraph_2')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_5.title')}</h3>
            <p>{t('privacy_page.section_5.paragraph_1')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_6.title')}</h3>
            <p>{t('privacy_page.section_6.paragraph_1')}</p>
            <p>
              {t('privacy_page.section_6.paragraph_2')}{' '}
              <a
                className={styles.link}
                href={t('privacy_page.section_6.link')}
                target="_blank"
                rel="noreferrer">
                {t('privacy_page.section_6.link')}
              </a>
            </p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_7.title')}</h3>
            <p>{t('privacy_page.section_7.paragraph_1')}</p>
            <p>{t('privacy_page.section_7.paragraph_2')}</p>
            <ul>
              <li>
                <span>{t('privacy_page.section_7.list.item_1')}</span>
              </li>
              <li>
                <span>{t('privacy_page.section_7.list.item_2')}</span>
              </li>
              <li>
                <span>{t('privacy_page.section_7.list.item_3')}</span>
              </li>
              <li>
                <span>{t('privacy_page.section_7.list.item_4')}</span>
              </li>
              <li>
                <span>{t('privacy_page.section_7.list.item_5')}</span>
              </li>
              <li>
                <span>{t('privacy_page.section_7.list.item_6')}</span>
              </li>
            </ul>

            <p>{t('privacy_page.section_7.paragraph_3')}</p>
            <p>{t('privacy_page.section_7.paragraph_4')}</p>
            <p>{t('privacy_page.section_7.paragraph_5')}</p>
            <p>{t('privacy_page.section_7.paragraph_6')}</p>
            <p>{t('privacy_page.section_7.paragraph_7')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_8.title')}</h3>
            <p>{t('privacy_page.section_8.paragraph_1')}</p>
            <p>{t('privacy_page.section_8.paragraph_2')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_9.title')}</h3>
            <p>{t('privacy_page.section_9.paragraph_1')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_10.title')}</h3>
            <p>{t('privacy_page.section_10.paragraph_1')}</p>
            <p>{t('privacy_page.section_10.paragraph_2')}</p>
            <p>{t('privacy_page.section_10.paragraph_3')}</p>
            <p>{t('privacy_page.section_10.paragraph_4')}</p>
            <p>{t('privacy_page.section_10.paragraph_5')}</p>
            <p>{t('privacy_page.section_10.paragraph_6')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_11.title')}</h3>
            <p>{t('privacy_page.section_11.paragraph_1')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_12.title')}</h3>
            <p>{t('privacy_page.section_12.paragraph_1')}</p>
            <p>{t('privacy_page.section_12.paragraph_2')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_13.title')}</h3>
            <p>{t('privacy_page.section_13.paragraph_1')}</p>
            <p>{t('privacy_page.section_13.paragraph_2')}</p>
            <p>{t('privacy_page.section_13.paragraph_3')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_14.title')}</h3>
            <p>{t('privacy_page.section_14.paragraph_1')}</p>
          </div>
          <div className={styles.section}>
            <h3>{t('privacy_page.section_15.title')}</h3>
            <p>{t('privacy_page.section_15.paragraph_1')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

PrivacyPage.getLayout = (page: ReactNode) => <PrivateLayout>{page}</PrivateLayout>;

export default PrivacyPage;

export const getServerSideProps = async ({ locale }: GetServerSidePropsContext) => ({
  props: {
    ...(await serverSideTranslations(locale!, ['common'])),
  },
});
